import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { campaignsApi, syncApi, statsApi } from "@/api";
import type { Campaign, CampaignStatus } from "@/types";
import { format, formatDistanceToNow } from "date-fns";
import { ja } from "date-fns/locale";
import {
  Plus, Send, RefreshCw, Users, Mail, Eye, MousePointer, DollarSign,
  Flame, MessageSquare, FileText, Phone, CalendarClock,
} from "lucide-react";

const statusColors: Record<CampaignStatus, string> = {
  DRAFT: "bg-slate-100 text-slate-700",
  SCHEDULED: "bg-blue-100 text-blue-700",
  SENDING: "bg-amber-100 text-amber-700",
  PAUSED: "bg-red-100 text-red-700",
  SENT: "bg-green-100 text-green-700",
  CANCELLED: "bg-red-100 text-red-700",
};

const statusLabels: Record<CampaignStatus, string> = {
  DRAFT: "下書き",
  SCHEDULED: "予約済",
  SENDING: "送信中",
  PAUSED: "安全停止",
  SENT: "送信済",
  CANCELLED: "キャンセル",
};

const CHANNEL_DISPLAY: Record<string, { label: string; icon: typeof Mail; color: string }> = {
  EMAIL: { label: "メール", icon: Mail, color: "text-blue-500" },
  SMS: { label: "SMS", icon: MessageSquare, color: "text-green-500" },
  LINE: { label: "LINE", icon: MessageSquare, color: "text-[#06C755]" },
  FAX: { label: "FAX", icon: FileText, color: "text-orange-500" },
  CALL: { label: "架電", icon: Phone, color: "text-purple-500" },
};

const campaignFilters = [
  { value: "ALL", label: "すべて" },
  { value: "SCHEDULED", label: "予約中" },
  { value: "DRAFT", label: "下書き" },
  { value: "SENDING", label: "送信中" },
  { value: "PAUSED", label: "安全停止" },
  { value: "SENT", label: "送信済" },
  { value: "CANCELLED", label: "キャンセル" },
] as const;

function CampaignCard({ campaign }: { campaign: Campaign }) {
  const navigate = useNavigate();
  const target = campaign.status === "DRAFT"
    ? `/campaigns/${campaign.id}/edit`
    : `/campaigns/${campaign.id}`;

  return (
    <div
      onClick={() => navigate(target)}
      className="bg-white p-4 rounded-lg shadow-sm border hover:border-blue-300 cursor-pointer transition-colors"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm truncate">{campaign.name}</h3>
          <p className="text-xs text-slate-500 mt-1 truncate">{campaign.subject}</p>
        </div>
        <div className="flex items-center gap-2">
          {campaign.segmentCount != null && (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
              <Users size={10} className="inline mr-1" />{campaign.segmentCount.toLocaleString()}件
            </span>
          )}
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[campaign.status]}`}>
            {statusLabels[campaign.status]}
          </span>
        </div>
      </div>
      <div className="flex gap-4 mt-3 text-xs text-slate-500">
        <span>送信: {campaign.totalSent}</span>
        <span>開封: {campaign.totalOpened}</span>
        <span>クリック: {campaign.totalClicked}</span>
        {(campaign as any).hotLeadCount > 0 && (
          <span className="text-orange-600 font-medium">
            <Flame size={10} className="inline mr-0.5" />
            {(campaign as any).hotLeadCount}
          </span>
        )}
      </div>
      <p className="text-xs text-slate-400 mt-2">
        {campaign.sentAt
          ? `送信: ${format(new Date(campaign.sentAt), "yyyy/MM/dd HH:mm")}`
          : campaign.scheduledAt
            ? `予約: ${format(new Date(campaign.scheduledAt), "yyyy/MM/dd HH:mm")}`
            : `作成: ${format(new Date(campaign.createdAt), "yyyy/MM/dd HH:mm")}`}
      </p>
    </div>
  );
}

function ScheduledCampaigns({ campaigns }: { campaigns: Campaign[] }) {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-blue-500" />
          <h3 className="text-sm font-bold">送信予約</h3>
        </div>
        <span className="text-xs text-slate-500">{campaigns.length.toLocaleString()}件</span>
      </div>
      {campaigns.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-slate-400">予約中のキャンペーンはありません</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-slate-600">予約日時</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">キャンペーン</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">宛先リスト</th>
                <th className="text-right px-4 py-2 font-medium text-slate-600">対象</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {campaigns.map((campaign) => (
                <tr
                  key={campaign.id}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/campaigns/${campaign.id}`)}
                >
                  <td className="px-4 py-2 whitespace-nowrap font-medium text-blue-700">
                    {campaign.scheduledAt ? format(new Date(campaign.scheduledAt), "yyyy/MM/dd HH:mm") : "-"}
                  </td>
                  <td className="px-4 py-2 min-w-[280px]">
                    <p className="font-medium text-slate-900">{campaign.name}</p>
                    <p className="text-xs text-slate-500 truncate max-w-[420px]">{campaign.subject}</p>
                  </td>
                  <td className="px-4 py-2 min-w-[220px] text-slate-600">
                    {campaign.audienceList?.name || "-"}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap text-slate-600">
                    {campaign.segmentCount != null ? `${campaign.segmentCount.toLocaleString()}件` : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [campaignFilter, setCampaignFilter] = useState<(typeof campaignFilters)[number]["value"]>("ALL");

  const { data, isLoading } = useQuery({
    queryKey: ["campaigns"],
    queryFn: () => campaignsApi.list(),
  });

  const { data: stats } = useQuery({
    queryKey: ["stats-overview"],
    queryFn: () => statsApi.overview(),
  });

  const { data: hotLeads } = useQuery({
    queryKey: ["stats-hot-leads"],
    queryFn: () => statsApi.hotLeads(),
  });

  const { data: channelStats } = useQuery({
    queryKey: ["stats-channels"],
    queryFn: () => statsApi.channels(),
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const [contacts, unsubs] = await Promise.all([
        syncApi.contacts(),
        syncApi.unsubscribes(),
      ]);
      return { contacts, unsubs };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["stats-overview"] });
    },
  });

  const campaigns = useMemo(() => data?.data ?? [], [data?.data]);
  const scheduledCampaigns = useMemo(
    () => campaigns
      .filter((campaign) => campaign.status === "SCHEDULED")
      .sort((a, b) => {
        const aTime = a.scheduledAt ? new Date(a.scheduledAt).getTime() : 0;
        const bTime = b.scheduledAt ? new Date(b.scheduledAt).getTime() : 0;
        return aTime - bTime;
      }),
    [campaigns],
  );
  const visibleCampaigns = campaignFilter === "ALL"
    ? campaigns
    : campaigns.filter((campaign) => campaign.status === campaignFilter);

  return (
    <div className="space-y-6">
      {/* Stats overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-1">
              <Users size={14} className="text-slate-500" />
              <span className="text-xs text-slate-500">総コンタクト</span>
            </div>
            <p className="text-2xl font-bold">{stats.totalContacts.toLocaleString()}</p>
            <div className="flex gap-2 mt-1 text-xs text-slate-400">
              <span>有効: {stats.contactsByStatus.ACTIVE?.toLocaleString() || 0}</span>
              <span>停止: {stats.contactsByStatus.UNSUBSCRIBED?.toLocaleString() || 0}</span>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-1">
              <Mail size={14} className="text-slate-500" />
              <span className="text-xs text-slate-500">ソース内訳</span>
            </div>
            <div className="space-y-1 mt-2 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">取引先</span>
                <span className="font-medium">{stats.contactsBySource.TRADER?.toLocaleString() || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">担当者</span>
                <span className="font-medium">{stats.contactsBySource.TRADER_CONTACT?.toLocaleString() || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">会員</span>
                <span className="font-medium">{stats.contactsBySource.MEMBER?.toLocaleString() || 0}</span>
              </div>
            </div>
          </div>
          {stats.recentCampaigns.length > 0 && (
            <>
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-2 mb-1">
                  <Eye size={14} className="text-green-500" />
                  <span className="text-xs text-slate-500">平均開封率</span>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  {(stats.recentCampaigns.reduce((a, c) => a + c.openRate, 0) / stats.recentCampaigns.length).toFixed(1)}%
                </p>
                <p className="text-xs text-slate-400 mt-1">直近{stats.recentCampaigns.length}キャンペーン</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-2 mb-1">
                  <MousePointer size={14} className="text-blue-500" />
                  <span className="text-xs text-slate-500">平均クリック率</span>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  {(stats.recentCampaigns.reduce((a, c) => a + c.clickRate, 0) / stats.recentCampaigns.length).toFixed(1)}%
                </p>
                <p className="text-xs text-slate-400 mt-1">直近{stats.recentCampaigns.length}キャンペーン</p>
              </div>
            </>
          )}
          {(stats.conversionRevenue30d !== undefined && stats.conversionRevenue30d > 0) && (
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign size={14} className="text-amber-500" />
                <span className="text-xs text-slate-500">帰属売上（30日）</span>
              </div>
              <p className="text-2xl font-bold text-amber-600">
                {Math.round(stats.conversionRevenue30d).toLocaleString()}
              </p>
              <p className="text-xs text-slate-400 mt-1">{stats.conversionCount30d}件のコンバージョン</p>
            </div>
          )}
        </div>
      )}

      {/* Channel breakdown + Hot leads row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Channel breakdown */}
        {channelStats && Object.keys(channelStats.channels).length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50">
              <h3 className="text-sm font-bold">チャネル別実績</h3>
            </div>
            <div className="p-4 space-y-3">
              {Object.entries(channelStats.channels).map(([ch, s]) => {
                const cfg = CHANNEL_DISPLAY[ch];
                if (!cfg) return null;
                const Icon = cfg.icon;
                const successRate = s.total > 0 ? Math.round((s.sent / s.total) * 100) : 0;
                return (
                  <div key={ch} className="flex items-center gap-3">
                    <Icon size={16} className={cfg.color} />
                    <span className="text-sm font-medium w-16">{cfg.label}</span>
                    <div className="flex-1">
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-400 rounded-full"
                          style={{ width: `${successRate}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 w-32 text-right">
                      {s.sent.toLocaleString()} / {s.total.toLocaleString()}
                      <span className="ml-1 font-medium">({successRate}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Hot leads */}
        {hotLeads?.data && hotLeads.data.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
              <Flame size={14} className="text-orange-500" />
              <h3 className="text-sm font-bold">ホットリード TOP10</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-1.5 font-medium text-slate-600 text-xs">会社/メール</th>
                  <th className="text-right px-4 py-1.5 font-medium text-slate-600 text-xs">スコア</th>
                  <th className="text-right px-4 py-1.5 font-medium text-slate-600 text-xs">最終接触</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {hotLeads.data.map((lead) => (
                  <tr
                    key={lead.id}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/contacts/${lead.id}`)}
                  >
                    <td className="px-4 py-2">
                      <p className="font-medium text-xs truncate max-w-[200px]">{lead.company || lead.email}</p>
                      {lead.company && <p className="text-xs text-slate-400 truncate">{lead.email}</p>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span className="text-sm font-bold text-orange-600">{lead.leadScore}</span>
                    </td>
                    <td className="px-4 py-2 text-right text-xs text-slate-500">
                      {lead.lastEngagedAt
                        ? formatDistanceToNow(new Date(lead.lastEngagedAt), { addSuffix: true, locale: ja })
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent campaign performance */}
      {stats && stats.recentCampaigns.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50">
            <h3 className="text-sm font-bold">直近キャンペーン実績</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-slate-600">キャンペーン</th>
                <th className="text-right px-4 py-2 font-medium text-slate-600">送信</th>
                <th className="text-right px-4 py-2 font-medium text-slate-600">開封率</th>
                <th className="text-right px-4 py-2 font-medium text-slate-600">クリック率</th>
                <th className="text-right px-4 py-2 font-medium text-slate-600">送信日</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {stats.recentCampaigns.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/campaigns/${c.id}`)}
                >
                  <td className="px-4 py-2 font-medium">{c.name}</td>
                  <td className="px-4 py-2 text-right">{c.totalSent.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">
                    <span className="text-green-600 font-medium">{c.openRate}%</span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className="text-blue-600 font-medium">{c.clickRate}%</span>
                  </td>
                  <td className="px-4 py-2 text-right text-slate-500">
                    {c.sentAt ? format(new Date(c.sentAt), "MM/dd HH:mm") : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ScheduledCampaigns campaigns={scheduledCampaigns} />

      {/* Campaign list header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">キャンペーン</h2>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {campaignFilters.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setCampaignFilter(filter.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border ${
                  campaignFilter === filter.value
                    ? "bg-blue-50 border-blue-200 text-blue-700"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="flex items-center gap-2 px-3 py-2 text-sm border rounded-md hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw size={16} className={syncMutation.isPending ? "animate-spin" : ""} />
            {syncMutation.isPending ? "同期中..." : "コンタクト同期"}
          </button>
          <button
            onClick={() => navigate("/campaigns/new")}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
          >
            <Plus size={16} />
            新規キャンペーン
          </button>
        </div>
      </div>

      {syncMutation.isSuccess && (
        <div className="text-sm text-green-600 bg-green-50 px-4 py-2 rounded">
          同期完了: {syncMutation.data.contacts.upserted}件更新, {syncMutation.data.unsubs.count}件配信停止
        </div>
      )}

      {isLoading ? (
        <p className="text-slate-500">読み込み中...</p>
      ) : !campaigns.length ? (
        <div className="text-center py-12 text-slate-400">
          <Send size={48} className="mx-auto mb-4 opacity-50" />
          <p>キャンペーンがありません</p>
          <p className="text-sm mt-1">「新規キャンペーン」ボタンから作成してください</p>
        </div>
      ) : !visibleCampaigns.length ? (
        <div className="text-center py-12 text-slate-400">
          <Send size={48} className="mx-auto mb-4 opacity-50" />
          <p>該当するキャンペーンがありません</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleCampaigns.map((c) => (
            <CampaignCard key={c.id} campaign={c} />
          ))}
        </div>
      )}
    </div>
  );
}
